import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import AgoraRTC, { AgoraRTCProvider, useRTCClient } from "agora-rtc-react";
import CareManagerVideoCall from "../components/Agora/CareManagerVideoCall";
import {
  clearVideoCallSession,
  loadVideoCallSession,
  loadVideoSessionDevices,
  saveVideoCallSession,
} from "../utils/videoSessionDevices";
import "./VideoCallPage.css";

const isCareManagerProvider = (provider) =>
  provider?.role === "careManager" || provider?.providerType === "Care Manager";

const VideoCallPage = () => {
  const [agoraCredentials, setAgoraCredentials] = useState(null);
  const [appointment, setAppointment] = useState(null);
  const [authToken, setAuthToken] = useState(null);
  const [devicePreferences, setDevicePreferences] = useState(null);
  const [isCareManager, setIsCareManager] = useState(false);
  const [provider, setProvider] = useState(null);
  const [user, setUser] = useState(null);
  const [sessionStatus, setSessionStatus] = useState("loading");

  const location = useLocation();
  const navigate = useNavigate();

  const agoraClient = useRTCClient(
    AgoraRTC.createClient({ codec: "vp8", mode: "rtc" })
  );

  const handleClose = () => {
    clearVideoCallSession();
    window.close();

    setTimeout(() => {
      navigate("/appointments");
    }, 100);
  };

  const hydrateSession = useCallback((session) => {
    const {
      agoraCredentials: credentials,
      appointment: apt,
      authToken: token,
      devicePreferences: preferences,
      isCareManager: careManagerFlag,
      provider: providerData,
      user: userData,
    } = session || {};

    const hasValidCredentials =
      credentials?.agoraAppId &&
      credentials?.agoraChannelName &&
      credentials?.agoraAppToken;

    if (hasValidCredentials && apt && userData && providerData) {
      setAgoraCredentials(credentials);
      setAppointment(apt);
      setAuthToken(token || null);
      setProvider(providerData);
      setUser(userData);
      const isCareManager =
        careManagerFlag ?? isCareManagerProvider(providerData);

      setIsCareManager(isCareManager);
      setDevicePreferences(preferences || loadVideoSessionDevices());
      setSessionStatus("ready");
      return true;
    }

    return false;
  }, []);

  useEffect(() => {
    if (location.state && hydrateSession(location.state)) {
      saveVideoCallSession(location.state);
      return undefined;
    }

    const storedSession = loadVideoCallSession();
    if (storedSession && hydrateSession(storedSession)) {
      return undefined;
    }

    const handleMessage = (event) => {
      if (event.origin !== window.location.origin) return;

      if (event.data?.type === "VIDEO_CALL_DATA") {
        if (hydrateSession(event.data.data)) {
          saveVideoCallSession(event.data.data);
        } else {
          setSessionStatus("error");
        }
      }
    };

    window.addEventListener("message", handleMessage);

    const pollId = window.setInterval(() => {
      const session = loadVideoCallSession();
      if (session && hydrateSession(session)) {
        window.clearInterval(pollId);
      }
    }, 200);

    const timeoutId = window.setTimeout(() => {
      window.clearInterval(pollId);
      setSessionStatus((current) => (current === "loading" ? "error" : current));
    }, 8000);

    return () => {
      window.removeEventListener("message", handleMessage);
      window.clearInterval(pollId);
      window.clearTimeout(timeoutId);
    };
  }, [hydrateSession, location.state]);

  useEffect(() => {
    if (sessionStatus !== "error") return undefined;

    const redirectId = window.setTimeout(() => {
      navigate("/appointments", { replace: true });
    }, 2500);

    return () => window.clearTimeout(redirectId);
  }, [navigate, sessionStatus]);

  if (sessionStatus === "loading" && !agoraCredentials) {
    return (
      <div className="video-call-page">
        <div className="video-call-page-status">
          <p>Joining video session...</p>
        </div>
      </div>
    );
  }

  if (sessionStatus === "error" || !agoraCredentials || !appointment || !provider || !user) {
    return (
      <div className="video-call-page">
      <div className="video-call-page-status is-error">
        <p>Unable to start the video session.</p>
        <p>
          Please return to the waiting room and click Start Session again.
        </p>
      </div>
      </div>
    );
  }

  return (
    <div className="video-call-page">
      <AgoraRTCProvider client={agoraClient}>
        {/* Care managers and therapists share one call screen, including the
            waiting-room admit/dismiss notifications. Therapy sessions also keep a
            transcript and leave status updates to the notes workflow. */}
        <CareManagerVideoCall
          appId={agoraCredentials.agoraAppId}
          appointment={appointment}
          authToken={authToken}
          channelName={agoraCredentials.agoraChannelName}
          devicePreferences={devicePreferences}
          enableTranscription={!isCareManager}
          handleClose={handleClose}
          isJoin={true}
          markCompletedOnEnd={isCareManager}
          provider={provider}
          token={agoraCredentials.agoraAppToken}
          uid={103}
          user={user}
        />
      </AgoraRTCProvider>
    </div>
  );
};

export default VideoCallPage;
